import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AdminPasswordConfirmDialog } from './AdminPasswordConfirmDialog';
import { getAllPricingRules, updatePricingRule, reloadPricingCache, type CreditPricingRule, type CreditPricingRuleUpdate } from '@/api/pricing';
import { DollarSign, Edit, RefreshCw, Save, X, Zap } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface EditingRule {
  ruleId: string;
  credits_per_minute?: number;
  credits_per_unit?: number;
  description?: string;
}

export const PricingConfigurationPanel: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingRule, setEditingRule] = useState<EditingRule | null>(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{ ruleId: string; update: CreditPricingRuleUpdate } | null>(null);

  // Fetch pricing rules
  const { data: rules, isLoading, error } = useQuery({
    queryKey: ['pricing-rules'],
    queryFn: getAllPricingRules,
  });

  // Update pricing rule mutation
  const updateMutation = useMutation({
    mutationFn: ({ ruleId, update, password }: { ruleId: string; update: CreditPricingRuleUpdate; password: string }) =>
      updatePricingRule(ruleId, update, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      toast({
        title: "Pricing Updated",
        description: "Pricing rule has been updated successfully.",
      });
      setEditingRule(null);
      setPendingUpdate(null);
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update pricing rule.",
        variant: "destructive",
      });
    },
  });

  // Reload cache mutation
  const reloadMutation = useMutation({
    mutationFn: reloadPricingCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-rates'] });
      toast({
        title: "Cache Reloaded",
        description: "Pricing cache has been reloaded successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Reload Failed",
        description: error.message || "Failed to reload pricing cache.",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (rule: CreditPricingRule) => {
    setEditingRule({
      ruleId: rule.id,
      credits_per_minute: rule.credits_per_minute,
      credits_per_unit: rule.credits_per_unit,
      description: rule.description,
    });
  };

  const handleCancelEdit = () => {
    setEditingRule(null);
  };

  const handleSaveClick = () => {
    if (!editingRule) return;

    const update: CreditPricingRuleUpdate = {
      credits_per_minute: editingRule.credits_per_minute,
      credits_per_unit: editingRule.credits_per_unit,
      description: editingRule.description,
    };

    setPendingUpdate({ ruleId: editingRule.ruleId, update });
    setShowPasswordDialog(true);
  };

  const handlePasswordConfirm = async (password: string) => {
    if (!pendingUpdate) return;

    await updateMutation.mutateAsync({
      ruleId: pendingUpdate.ruleId,
      update: pendingUpdate.update,
      password,
    });
  };

  const handleReloadCache = () => {
    reloadMutation.mutate();
  };

  const formatUserType = (userType: string) => {
    if (userType === 'all') return 'All Users';
    return userType.charAt(0).toUpperCase() + userType.slice(1) + 's';
  };

  const formatActivityType = (activityType: string) => {
    return activityType.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatTier = (tier: string | null) => {
    if (!tier) return 'All Tiers';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">
            Error loading pricing rules: {(error as Error).message}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group rules by user type and activity
  const audioRules = rules?.filter(r => r.activity_type === 'audio_generation') || [];
  const shortsRules = rules?.filter(r => r.activity_type === 'shorts_generation') || [];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Pricing Configuration
              </CardTitle>
              <CardDescription>
                Manage credit pricing rates for different user types and activities
              </CardDescription>
            </div>
            <Button
              onClick={handleReloadCache}
              disabled={reloadMutation.isPending}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${reloadMutation.isPending ? 'animate-spin' : ''}`} />
              Reload Cache
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Audio Generation Pricing */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Audio Generation Pricing (Credits per Minute)
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User Type</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Rate (Credits/Min)</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audioRules.map((rule) => {
                    const isEditing = editingRule?.ruleId === rule.id;
                    return (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">
                          {formatUserType(rule.user_type)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={rule.tier === 'pro' ? 'default' : 'secondary'}>
                            {formatTier(rule.tier)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              type="number"
                              step="0.1"
                              value={editingRule.credits_per_minute || ''}
                              onChange={(e) => setEditingRule({
                                ...editingRule,
                                credits_per_minute: parseFloat(e.target.value)
                              })}
                              className="w-24"
                            />
                          ) : (
                            <span className="font-mono">{rule.credits_per_minute}</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          {isEditing ? (
                            <Input
                              value={editingRule.description || ''}
                              onChange={(e) => setEditingRule({
                                ...editingRule,
                                description: e.target.value
                              })}
                              placeholder="Description"
                            />
                          ) : (
                            <span className="text-sm text-muted-foreground">{rule.description}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveClick}
                                disabled={updateMutation.isPending}
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(rule)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Shorts Generation Pricing */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Shorts Generation Pricing (Credits per Short)
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User Type</TableHead>
                    <TableHead>Rate (Credits/Short)</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shortsRules.map((rule) => {
                    const isEditing = editingRule?.ruleId === rule.id;
                    return (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">
                          {formatUserType(rule.user_type)}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              type="number"
                              value={editingRule.credits_per_unit || ''}
                              onChange={(e) => setEditingRule({
                                ...editingRule,
                                credits_per_unit: parseInt(e.target.value)
                              })}
                              className="w-24"
                            />
                          ) : (
                            <span className="font-mono">{rule.credits_per_unit}</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          {isEditing ? (
                            <Input
                              value={editingRule.description || ''}
                              onChange={(e) => setEditingRule({
                                ...editingRule,
                                description: e.target.value
                              })}
                              placeholder="Description"
                            />
                          ) : (
                            <span className="text-sm text-muted-foreground">{rule.description}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveClick}
                                disabled={updateMutation.isPending}
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(rule)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> Pricing changes require admin password confirmation and will take effect immediately
              after cache reload. Individual users pay 2 credits/min (base) or 1.5 credits/min (Pro).
              Practitioners pay 4 credits/min (base) or 3 credits/min (Pro).
            </p>
          </div>
        </CardContent>
      </Card>

      <AdminPasswordConfirmDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
        onConfirm={handlePasswordConfirm}
        title="Confirm Pricing Change"
        description="You are about to modify system pricing. This will affect all users immediately after cache reload. Please enter your admin password to confirm."
        actionLabel="Update Pricing"
      />
    </>
  );
};
